import { useEffect, useMemo, useState } from "react";
import { Eye, ExternalLink } from "lucide-react";
import { fetchCardPriceObservations, fetchCardPriceObservationHistory } from "../lib/api";
import { eur } from "../lib/prices";
import { Sparkline } from "./Sparkline";

// Bloc « Marché réel » de la fiche carte (maquette validée le 2026-10-03) :
// le prix RÉEL observé par Œil de Sauron devient le chiffre principal, la
// cote Cardmarket/TCGdex reste en référence avec l'écart, chaque état montre
// son mini + ses annonces + une mini-courbe, et une fenêtre d'évolution
// superpose réel (ambre) et cote (bleu) avec la fourchette min–max réelle.
// Masqué tant qu'aucune capture n'existe (jamais un bloc vide).
//
// Depuis le 2026-10-03 (soir) le bloc est en DEUX parties affichables
// séparément (`parts`) : « summary » (grand chiffre + états, colonne de
// droite de la fiche) et « chart » (l'évolution, sous le tableau Cardmarket
// à gauche — les deux courbes de prix dans la même colonne, plus de carré
// vide). Les données sont chargées une seule fois par `useRealMarketData`
// dans la fiche et passées en `data` ; sans `data`, le bloc charge seul.
//
// Codes d'état Cardmarket (badge de chaque annonce), du meilleur au moins bon
// - PAS les mêmes labels que CONDITION_GRADES (config/variants.js), qui sert
// à noter l'état de SES PROPRES cartes : ici ce sont les codes bruts que
// Cardmarket affiche, capturés tels quels par l'extension.
export const CM_CONDITION_ORDER = ["MT", "NM", "EX", "GD", "LP", "PL", "PO"];
export const CM_CONDITION_LABEL = {
  MT: "Mint", NM: "Near Mint", EX: "Excellent", GD: "Good",
  LP: "Light Played", PL: "Played", PO: "Poor",
};
export const OS_COLOR = "#f59e0b";   // ambre = donnée réelle Œil de Sauron
export const COTE_COLOR = "#60a5fa"; // bleu = cote théorique

const WINDOWS = [["7", "7 j"], ["30", "30 j"], ["90", "90 j"], ["all", "Tout"]];

const fmtDay = (d) => { const [, m, day] = d.split("-"); return `${day}/${m}`; };
const fmtDelta = (pct) => `${pct >= 0 ? "+" : "−"}${Math.abs(pct).toFixed(0)} %`;
const pctChange = (a, b) => (a && b ? ((b - a) / a) * 100 : null);

export const DeltaBadge = ({ pct, className = "" }) => {
  if (pct == null) return null;
  const up = pct >= 0;
  return (
    <span className={`inline-block text-[11px] font-bold rounded-md px-1.5 py-0.5 ${up ? "text-emerald-400 bg-emerald-400/10" : "text-red-400 bg-red-400/10"} ${className}`}>
      réel {fmtDelta(pct)}
    </span>
  );
};

/** Observations (dernière par état) + séries temporelles d'une carte. */
export const useRealMarketData = (cardId) => {
  const [obs, setObs] = useState({});
  const [hist, setHist] = useState(null);
  useEffect(() => {
    setObs({}); setHist(null);
    if (!cardId) return;
    let cancelled = false;
    fetchCardPriceObservations(cardId).then(o => { if (!cancelled) setObs(o); }).catch(() => { if (!cancelled) setObs({}); });
    fetchCardPriceObservationHistory(cardId).then(h => { if (!cancelled) setHist(h); }).catch(() => { if (!cancelled) setHist(null); });
    return () => { cancelled = true; };
  }, [cardId]);
  return { obs, hist };
};

// Courbe réel vs cote sur une fenêtre. Axe X = temps réel (pas l'indice),
// pour que les jours sans visite se voient comme des trous honnêtes.
const RealVsCoteChart = ({ real, cote, windowKey }) => {
  const [hover, setHover] = useState(null);
  const W = 520, H = 170, L = 42, R = 12, T = 14, B = 22;

  const model = useMemo(() => {
    const now = Date.now();
    const days = windowKey === "all" ? Infinity : parseInt(windowKey, 10);
    const from = now - days * 86400000;
    const ts = (d) => Date.parse(`${d}T12:00:00Z`);
    const r = real.filter(p => ts(p.day) >= from);
    const c = cote.filter(p => ts(p.day) >= from);
    if (r.length < 2) return null;
    const allT = [...r.map(p => ts(p.day)), ...c.map(p => ts(p.day))];
    const t0 = Math.min(...allT), t1 = Math.max(...allT);
    const vals = [...r.map(p => p.min), ...r.map(p => p.max), ...c.map(p => p.trend)];
    let min = Math.min(...vals), max = Math.max(...vals);
    if (max === min) { max += 1; min = Math.max(0, min - 1); }
    const pad = (max - min) * 0.08;
    min -= pad; max += pad;
    const x = (t) => L + (t1 === t0 ? 0 : ((t - t0) / (t1 - t0)) * (W - L - R));
    const y = (v) => T + (1 - (v - min) / (max - min)) * (H - T - B);
    const line = (arr, key) => arr.map((p, i) => `${i ? "L" : "M"}${x(ts(p.day)).toFixed(1)},${y(p[key]).toFixed(1)}`).join(" ");
    const band = `M${r.map(p => `${x(ts(p.day)).toFixed(1)},${y(p.max).toFixed(1)}`).join(" L")} L${[...r].reverse().map(p => `${x(ts(p.day)).toFixed(1)},${y(p.min).toFixed(1)}`).join(" L")} Z`;
    const ticks = [min + pad, (min + max) / 2, max - pad];
    return { r, c, x, y, ts, line, band, ticks };
  }, [real, cote, windowKey]);

  if (!model) {
    return (
      <div className="text-xs text-zinc-500 bg-zinc-800/40 rounded-lg p-3 text-center">
        Pas assez de visites sur cette période pour tracer une courbe (il en faut deux jours différents).
      </div>
    );
  }
  const { r, c, x, y, ts, line, band, ticks } = model;
  const hoverPt = hover != null ? r[hover] : null;
  const coteAt = (day) => { const f = c.find(p => p.day === day); return f ? f.trend : null; };

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: "auto" }} onMouseLeave={() => setHover(null)}>
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={L} x2={W - R} y1={y(t)} y2={y(t)} stroke="currentColor" className="text-zinc-800" strokeWidth="1" />
            <text x={L - 6} y={y(t) + 3} textAnchor="end" fontSize="9" className="fill-zinc-500">{t.toFixed(t >= 100 ? 0 : 1)} €</text>
          </g>
        ))}
        <path d={band} fill="#71717a" fillOpacity=".16" />
        {c.length > 1 && <path d={line(c, "trend")} fill="none" stroke={COTE_COLOR} strokeWidth="1.6" strokeDasharray="4 3" />}
        <path d={line(r, "min")} fill="none" stroke={OS_COLOR} strokeWidth="2" strokeLinejoin="round" />
        {r.map((p, i) => <circle key={i} cx={x(ts(p.day))} cy={y(p.min)} r={i === r.length - 1 ? 3.5 : 2} fill={OS_COLOR} />)}
        <text x={L} y={H - 6} fontSize="9" className="fill-zinc-500">{fmtDay(r[0].day)}</text>
        <text x={W - R} y={H - 6} textAnchor="end" fontSize="9" className="fill-zinc-500">{fmtDay(r[r.length - 1].day)}</text>
        {hoverPt && (
          <line x1={x(ts(hoverPt.day))} x2={x(ts(hoverPt.day))} y1={T} y2={H - B} stroke="currentColor" className="text-zinc-600" strokeDasharray="3 3" strokeWidth="1" />
        )}
        {r.map((p, i) => {
          const cx = x(ts(p.day));
          const prev = i > 0 ? x(ts(r[i - 1].day)) : L;
          const next = i < r.length - 1 ? x(ts(r[i + 1].day)) : W - R;
          const x0 = (prev + cx) / 2, x1 = (cx + next) / 2;
          return <rect key={`h${i}`} x={x0} y={T} width={Math.max(1, x1 - x0)} height={H - T - B} fill="transparent" onMouseEnter={() => setHover(i)} />;
        })}
      </svg>
      {hoverPt && (
        <div className="text-[11px] text-zinc-400 text-center -mt-1">
          {fmtDay(hoverPt.day)} · <span style={{ color: OS_COLOR }}>réel {eur(hoverPt.min)}</span>
          {hoverPt.max !== hoverPt.min && <span className="text-zinc-500"> (jusqu'à {eur(hoverPt.max)})</span>}
          {coteAt(hoverPt.day) != null && <span style={{ color: COTE_COLOR }}> · cote {eur(coteAt(hoverPt.day))}</span>}
        </div>
      )}
    </div>
  );
};

const OsBadge = ({ captures }) => (
  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold rounded-full px-2 py-0.5"
    style={{ color: OS_COLOR, background: "rgba(245,158,11,.14)", border: "1px solid rgba(245,158,11,.35)" }}>
    <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: OS_COLOR, boxShadow: `0 0 8px ${OS_COLOR}` }} />
    Œil de Sauron{captures > 0 && ` · ${captures} capture${captures > 1 ? "s" : ""}`}
  </span>
);

export const RealMarketBlock = ({ cardId, cote, onSeeAll, wide = false, data, parts = "all" }) => {
  const own = useRealMarketData(data ? null : cardId);
  const { obs, hist } = data || own;
  const [windowKey, setWindowKey] = useState("30");

  const conditions = CM_CONDITION_ORDER.filter(c => obs[c] && obs[c].prices?.length);
  // État de référence : Near Mint si observé, sinon le meilleur disponible.
  const primary = obs.NM ? "NM" : conditions[0];
  const series = useMemo(() => hist?.series || {}, [hist]);
  const realSeries = useMemo(() => (primary && series[primary]) || [], [series, primary]);
  const coteSeries = useMemo(() => hist?.cote || [], [hist]);

  // Résumé de la fenêtre : variation du réel et de la cote entre le premier
  // et le dernier point visibles. (Hook déclaré AVANT le retour anticipé
  // ci-dessous : règle des Hooks.)
  const summary = useMemo(() => {
    const days = windowKey === "all" ? Infinity : parseInt(windowKey, 10);
    const from = Date.now() - days * 86400000;
    const inWin = (p) => Date.parse(`${p.day}T12:00:00Z`) >= from;
    const r = realSeries.filter(inWin), c = coteSeries.filter(inWin);
    const realCh = r.length > 1 ? pctChange(r[0].min, r[r.length - 1].min) : null;
    const coteCh = c.length > 1 ? pctChange(c[0].trend, c[c.length - 1].trend) : null;
    const deltas = r.map(p => { const f = c.find(q => q.day === p.day); return f ? ((p.min - f.trend) / f.trend) * 100 : null; }).filter(v => v != null);
    const meanDelta = deltas.length ? deltas.reduce((a, b) => a + b, 0) / deltas.length : null;
    return { realCh, coteCh, meanDelta };
  }, [realSeries, coteSeries, windowKey]);

  if (conditions.length === 0) return null;

  const primaryPrices = [...obs[primary].prices].sort((a, b) => a - b);
  const real = primaryPrices[0];
  const delta = cote ? ((real - cote) / cote) * 100 : null;
  const captures = Object.values(series).reduce((n, arr) => Math.max(n, arr.length), 0);
  const showSummary = parts === "all" || parts === "summary";
  const showChart = parts === "all" || parts === "chart";

  return (
    <div className={`w-full bg-zinc-800/50 rounded-lg p-3 ${wide ? "" : "mt-3"}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-xs text-zinc-400 font-semibold uppercase tracking-wide">
          {parts === "chart" ? "Évolution du marché réel" : "Marché réel"}
        </span>
        {showSummary ? <OsBadge captures={captures} /> : (
          <span className="inline-flex bg-zinc-950 border border-zinc-700 rounded-lg p-0.5 gap-0.5">
            {WINDOWS.map(([k, label]) => (
              <button key={k} onClick={() => setWindowKey(k)}
                className={`text-[11px] font-semibold px-2 py-0.5 rounded-md ${windowKey === k ? "bg-zinc-700 text-white" : "text-zinc-400 hover:text-white"}`}>
                {label}
              </button>
            ))}
          </span>
        )}
      </div>

      {showSummary && (
        <div className={wide ? "grid grid-cols-1 sm:grid-cols-[auto_minmax(0,1fr)] gap-x-6 gap-y-2 items-start" : ""}>
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-zinc-500">
                {CM_CONDITION_LABEL[primary]} · {obs[primary].language?.toUpperCase() || "FR"} · {fmtDay(obs[primary].day)}
              </div>
              <div className="stat-value text-3xl leading-none mt-0.5" style={{ color: OS_COLOR }}>{eur(real)}</div>
            </div>
            {cote != null && (
              <div className="pb-0.5">
                <div className="text-xs text-zinc-400">Cote Cardmarket <b className="font-semibold" style={{ color: COTE_COLOR }}>{eur(cote)}</b></div>
                <DeltaBadge pct={delta} className="mt-1" />
              </div>
            )}
          </div>

          <div className={wide ? "" : "mt-3"}>
            {conditions.map((cond, i) => {
              const prices = [...obs[cond].prices].sort((a, b) => a - b);
              const spark = (series[cond] || []).map(p => p.min);
              // Deux lignes par état : état + mini + mini-courbe, puis les annonces.
              return (
                <div key={cond} className={`py-1.5 text-xs ${i ? "border-t border-zinc-700/50" : ""}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-300 font-semibold truncate">{CM_CONDITION_LABEL[cond] || cond}</span>
                    <span className="font-bold text-white text-sm tabular-nums">{eur(prices[0])}</span>
                    <Sparkline values={spark} color={OS_COLOR} className="ml-auto shrink-0" />
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {prices.map((p, k) => (
                      <span key={k} className="text-[11px] rounded px-1.5 py-px tabular-nums"
                        style={k === 0 ? { color: OS_COLOR, background: "rgba(245,158,11,.14)" } : { color: "#a1a1aa", background: "rgb(39 39 42)" }}>
                        {p.toFixed(2)}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showChart && (
        <div className={showSummary ? "mt-3 pt-3 border-t border-zinc-700/50" : ""}>
          {showSummary && (
            <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
              <span className="text-xs text-zinc-400 font-semibold uppercase tracking-wide">Évolution</span>
              <span className="inline-flex bg-zinc-950 border border-zinc-700 rounded-lg p-0.5 gap-0.5">
                {WINDOWS.map(([k, label]) => (
                  <button key={k} onClick={() => setWindowKey(k)}
                    className={`text-[11px] font-semibold px-2 py-0.5 rounded-md ${windowKey === k ? "bg-zinc-700 text-white" : "text-zinc-400 hover:text-white"}`}>
                    {label}
                  </button>
                ))}
              </span>
            </div>
          )}
          <div className="flex gap-3 flex-wrap text-[11px] text-zinc-400 mb-1">
            <span><i className="inline-block w-3.5 h-0.5 rounded align-middle mr-1" style={{ background: OS_COLOR }} />Réel, {CM_CONDITION_LABEL[primary]} (min.)</span>
            <span><i className="inline-block w-3.5 h-0.5 rounded align-middle mr-1" style={{ background: COTE_COLOR }} />Cote</span>
            <span><i className="inline-block w-3.5 h-0.5 rounded align-middle mr-1 bg-zinc-600" />Fourchette</span>
          </div>
          {hist === null ? (
            <div className="text-xs text-zinc-600">Chargement…</div>
          ) : (
            <RealVsCoteChart real={realSeries} cote={coteSeries} windowKey={windowKey} />
          )}
          {(summary.realCh != null || summary.coteCh != null) && (
            <div className="text-[11px] text-zinc-500 mt-2 flex gap-3 flex-wrap">
              {summary.realCh != null && <span>réel <b className="text-zinc-300">{fmtDelta(summary.realCh)}</b></span>}
              {summary.coteCh != null && <span>cote <b className="text-zinc-300">{fmtDelta(summary.coteCh)}</b></span>}
              {summary.meanDelta != null && <span>écart moyen <b className="text-zinc-300">{fmtDelta(summary.meanDelta)}</b></span>}
            </div>
          )}
        </div>
      )}

      {showSummary && onSeeAll && (
        <button onClick={onSeeAll} className="mt-3 inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white">
          <Eye className="w-3.5 h-3.5" />Toutes les cartes observées<ExternalLink className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};
